import { LongText } from "../processors/graphQL";
import { Unit } from "../processors/kpiReport.types";
import _ from "lodash";
import { EMPTY_ARRAY } from "./constants";
import { CategoryChart } from "../processors";

export const extractInitialYearWith1DimValues = (years: number[], values: number[], averages: number[]) => {
    return years?.find((year, index) => values[index] != null && averages[index] != null);
};

export const extractInitialYearWith2DimValues = (years: number[], values: number[][], averages: number[][]) => {
    return years.find((year, index) => {
        const hasAverageData: boolean =
            averages.find(av => {
                return av[index] != null;
            }) != null;
        const hasLinesData: boolean =
            values.find(lv => {
                return lv[index] != null;
            }) != null;

        return hasAverageData && hasLinesData;
    });
};

export const extractComparionChartData = (
    categoryChart: CategoryChart,
    chartData: {
        connectionSizeCategory: string;
        data: {
            unit?: Unit;
            year?: number;

            figure: {
                header?: LongText;
                value: number | string;
                decimals: number;
                footer?: LongText;
            };

            graph?: {
                header?: LongText;
                years: number[];
                measure: string;
                // largeProviderAverageData: number[];

                providerMedianSeriesName: string;
                providerMedianAnnotation: string;
                providerMedianData: number[];

                // largeProviderAverageSeriesName: string;
                lineData: number[];
                lineDataSeriesName: string;
                footer?: LongText;
            };
        };
    }[]
) => {
    const graphData = chartData?.map(d => d?.data?.graph);
    const _years = _.orderBy(_.uniq(_.flattenDeep(graphData.map(d => d.years))), d => d, "asc");
    const yearMapping = graphData.map((d, index) => {
        const yearToLineData =
            d?.lineData?.reduce((prev, cur, index) => {
                prev[d?.years?.[index]] = cur;
                return prev;
            }, {}) ?? null;

        const yearToAverageData =
            d?.providerMedianData?.reduce((prev, cur, index) => {
                prev[d?.years?.[index]] = cur;
                return prev;
            }, {}) ?? null;

        const connectionSizeCategory = chartData?.[index]?.connectionSizeCategory;
        return {
            connectionSizeCategory,
            yearToLineData,
            yearToAverageData
        };
    });

    const linesData: number[][] =
        yearMapping
            ?.map(d => {
                return d?.yearToLineData == null
                    ? null
                    : _years?.map(year => {
                          return d?.yearToLineData?.[year] as number;
                      });
            })
            ?.filter(d => d != null) ?? EMPTY_ARRAY;

    const averageData: number[][] =
        yearMapping
            ?.map(d => {
                return d?.yearToAverageData == null
                    ? null
                    : _years?.map(year => {
                          return d?.yearToAverageData?.[year] as number;
                      });
            })
            ?.filter(d => d != null) ?? EMPTY_ARRAY;

    // rebuild years, line data, media data
    const initialYear = extractInitialYearWith2DimValues(_years, linesData, averageData);
    const latestYear = extractInitialYearWith2DimValues(
        _.reverse([..._years]),
        linesData.map(d => _.reverse(d)),
        averageData.map(d => _.reverse(d))
    );

    const displayedYears = _years.filter(year => year >= initialYear && year <= latestYear);

    const displayedLinesData =
        yearMapping
            ?.map(d => {
                return d?.yearToLineData == null
                    ? null
                    : displayedYears?.map(year => {
                          return d?.yearToLineData?.[year] as number;
                      });
            })
            ?.filter(d => d != null) ?? EMPTY_ARRAY;

    const displayedAverageData =
        _.uniqBy(yearMapping, d => d.connectionSizeCategory)
            ?.map(d => {
                return d?.yearToAverageData == null
                    ? null
                    : displayedYears?.map(year => {
                          return d?.yearToAverageData?.[year] as number;
                      });
            })
            ?.filter(d => d != null) ?? EMPTY_ARRAY;

    // generate header, footer
    const visHeader = categoryChart.visHeader;
    const visFooter = categoryChart.visFooter;
    const visHeaderProcessed = _.template(visHeader?.value ?? "")({
        startYear: initialYear,
        endYear: latestYear
    });
    const visFooterProcessed = _.template(visFooter?.value ?? "")({
        startYear: initialYear,
        endYear: latestYear
    });

    const header =
        !visHeaderProcessed || visHeaderProcessed.trim() === ""
            ? null
            : {
                  processed: visHeaderProcessed,
                  value: visHeaderProcessed,
                  format: visHeaderProcessed
              };

    const footer =
        !visFooterProcessed || visFooterProcessed.trim() === ""
            ? null
            : {
                  processed: visFooterProcessed,
                  value: visFooterProcessed,
                  format: visFooterProcessed
              };

    const providerMedianSeriesNames = _.uniqBy(chartData, d => d.connectionSizeCategory).map(d => {
        return d?.data?.graph?.providerMedianSeriesName;
    });

    const providerMedianAnnotations = _.uniqBy(chartData, d => d.connectionSizeCategory).map(d => {
        return d?.data?.graph?.providerMedianAnnotation;
    });

    return {
        header,
        footer,
        years: displayedYears,
        latestYear: latestYear,
        displayedAverageData,
        displayedLinesData,
        unit: chartData?.[0]?.data?.unit,

        measure: chartData?.[0]?.data?.graph?.measure,
        figures: chartData?.map(d => d?.data?.figure),
        providerMedianSeriesNames,
        providerMedianAnnotations
    };
};
